import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import { SvgIcon } from '@mui/material'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { ReactComponent as CloseIcon } from '../../../../../assets/images/Close.svg'
import { TppCellMetric } from '../../../../../shared/queries/metrics/useGetTppElementSupportQuery'
import { ActivityStatusDetails } from '../../../../../shared/hooks/useActivityStatuses/useActivityStatuses.types'
import useActivityStatuses from '../../../../../shared/hooks/useActivityStatuses'
import { NO_STATUS_LEGEND_ITEM, UNCATEGORIZED_LEGEND_ITEM } from '../useTppElementSupport'
import { normalizeActivityStatus } from '../../../../../shared/hooks/useActivityStatuses/useActivityStatuses.helpers'
import { OverflowTooltipWrapper } from '../../../../../shared/components/OverflowTooltipWrapper/OverflowTooltipWrapper'
import { toDisplayValue } from '../../../../../shared/utils/displayValue'

interface TppCellDetailListProps {
    cells: TppCellMetric[]
    title: string
    total: number
    handleClose: () => void
}

const TppCellDetailList = ({ cells, title, total, handleClose }: TppCellDetailListProps) => {
    const location = useLocation()
    const { activityStatusDetails } = useActivityStatuses()

    // The org's configured entry for a status, or undefined when the status is blank or sits
    // outside the org's config. Resolved straight from useActivityStatuses rather than from the
    // donut legend: that legend is pruned to the statuses that won a cell, and this list renders
    // every activity, so gating on it silently drops rows.
    const resolveStatusDetails = (status?: string | null): ActivityStatusDetails | undefined => {
        const normalizedStatus = normalizeActivityStatus(status)
        if (!normalizedStatus) return undefined
        return activityStatusDetails.find((statusDetails) => statusDetails.id === normalizedStatus)
    }

    // Unconfigured statuses have no place in the ordering — sort them last.
    const getStatusOrder = (status?: string | null): number =>
        resolveStatusDetails(status)?.order ?? Number.MIN_SAFE_INTEGER

    const getBestOrder = (cell: TppCellMetric): number => {
        if (!cell.activities || cell.activities.length === 0) return -1
        return Math.max(...cell.activities.map((a) => getStatusOrder(a.status)))
    }

    const sortedCells = [...cells].sort((a, b) => getBestOrder(b) - getBestOrder(a))

    return (
        <Dialog
            open
            onClose={handleClose}
            fullWidth
            aria-labelledby="tpp-cell-detail-dialog-title"
            maxWidth="md"
            PaperProps={{
                sx: {
                    py: 4,
                    px: 8,
                    borderRadius: '24px',
                    boxShadow: '0px 15px 50px 0px rgba(0, 0, 0, 0.30)',
                },
            }}
        >
            <DialogTitle id="tpp-cell-detail-dialog-title" sx={{ p: 0 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    pb={1.5}
                    borderBottom="1px solid"
                    borderColor="figma.grayscale.stroke"
                >
                    <Stack direction="row" alignItems="center" gap={1.5}>
                        <Typography variant="22x600" color="figma.grayscale.textHier1">
                            {title}
                        </Typography>
                        <Typography variant="16x600" color="figma.grayscale.textHier2">
                            {total} count
                        </Typography>
                    </Stack>
                    <IconButton onClick={handleClose} size="small" sx={{ p: 0.5, color: 'figma.grayscale.textHier2' }}>
                        <SvgIcon component={CloseIcon} inheritViewBox fontSize="medium" color="inherit" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <Stack direction="column" gap={0.5} pt={2} pb={1}>
                    {sortedCells.map((cell, index) => {
                        const hasActivities = cell.activities && cell.activities.length > 0

                        const sortedActivities = hasActivities
                            ? [...cell.activities].sort((a, b) => getStatusOrder(b.status) - getStatusOrder(a.status))
                            : []

                        return (
                            <Stack key={cell._id} direction="column" gap={0.25} py={0.5}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                    sx={{ overflow: 'hidden', minWidth: 0 }}
                                >
                                    <Typography
                                        variant="16x400"
                                        color="figma.grayscale.textHier2"
                                        width="24px"
                                        textAlign="center"
                                        flexShrink={0}
                                    >
                                        {`${index + 1}.`}
                                    </Typography>
                                    <OverflowTooltipWrapper
                                        placement="top-start"
                                        arrow
                                        title={
                                            <Stack gap={1} overflow="scroll">
                                                <Typography
                                                    variant="16x400"
                                                    color="figma.grayscale.textHier1"
                                                    sx={{
                                                        whiteSpace: 'pre-line',
                                                        overflow: 'scroll',
                                                        maxHeight: '50vh',
                                                    }}
                                                >
                                                    {toDisplayValue(cell.content)}
                                                </Typography>
                                            </Stack>
                                        }
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    backgroundColor: 'figma.grayscale.tier7',
                                                    px: 2,
                                                    py: 1,
                                                    borderRadius: '6px',
                                                    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20);',
                                                },
                                            },
                                            arrow: {
                                                sx: { color: 'figma.grayscale.tier7' },
                                            },
                                        }}
                                    >
                                        <Typography variant="16x400" color="figma.grayscale.textHier1" noWrap>
                                            {toDisplayValue(cell.content)}
                                        </Typography>
                                    </OverflowTooltipWrapper>
                                </Stack>

                                <Stack direction="column" gap={0.25} pl={4}>
                                    {hasActivities
                                        ? sortedActivities.map((activity) => {
                                              // Always resolves, so every linked activity renders.
                                              const statusDetails = resolveStatusDetails(activity.status)
                                              const { icon, color, label } = statusDetails
                                                  ? {
                                                        icon: statusDetails.icon,
                                                        color: statusDetails.color,
                                                        label: statusDetails.name,
                                                    }
                                                  : activity.status == null
                                                    ? NO_STATUS_LEGEND_ITEM
                                                    : UNCATEGORIZED_LEGEND_ITEM
                                              return (
                                                  <Stack
                                                      key={activity._id.toString()}
                                                      direction="row"
                                                      alignItems="center"
                                                      gap={0.75}
                                                  >
                                                      <SvgIcon
                                                          component={icon}
                                                          inheritViewBox
                                                          fontSize="small"
                                                          style={{
                                                              fontSize: '14px',
                                                              color,
                                                              flexShrink: 0,
                                                          }}
                                                      />
                                                      <Typography
                                                          variant="14x400"
                                                          color="figma.grayscale.textHier2"
                                                          flexShrink={0}
                                                          minWidth="100px"
                                                      >
                                                          {label}
                                                      </Typography>
                                                      {activity.title && (
                                                          <Link
                                                              component={RouterLink}
                                                              underline="none"
                                                              to={{
                                                                  pathname: '/activities',
                                                                  autoOpenData: {
                                                                      isAutoOpenPanel: true,
                                                                      autoOpenData: activity,
                                                                      previousDataStack: [{ path: location.pathname }],
                                                                  },
                                                              }}
                                                              sx={{
                                                                  '&:hover .activity-title': {
                                                                      textDecoration: 'underline',
                                                                      color: 'figma.primary',
                                                                  },
                                                              }}
                                                          >
                                                              <Typography
                                                                  className="activity-title"
                                                                  variant="14x400"
                                                                  color="figma.grayscale.textHier2"
                                                              >
                                                                  {activity.title}
                                                              </Typography>
                                                          </Link>
                                                      )}
                                                  </Stack>
                                              )
                                          })
                                        : null}
                                </Stack>
                            </Stack>
                        )
                    })}
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

export default TppCellDetailList
